import { RefObject, useCallback, useEffect, useMemo } from 'react';
import { ScrollIntoViewProps, useScrollIntoView } from './useScrollIntoView';

type Props<T extends string | number = number> = {
  listingIds: T[];
  openListingId: T | null;
  onNavigate?: (listingId: T) => void;
  onPrev?: () => void;
  onNext?: () => void;
  containerRef?: RefObject<HTMLDivElement> | null;
  isDisabledScrollIntoView?: boolean;
};
/**
 * - 리스팅의 hasPrev, hasNext, onPrev, onNext 기능 제공
 * - isDisabledScrollIntoView = true 일 경우 className 내 `.listing_${openListingId}` 포함되어 있는 엘리먼트로 스크롤을 이동 기능 제공
 * @param listingIds
 * @param openListingId
 * @param onNavigate
 * @param containerRef
 * @param isDisabledScrollIntoView
 * @param scrollOffset
 * @param scrollBehavior
 * @param scrollBlock
 */
export const useListingNavigation = <T extends string | number = number>({
  listingIds,
  openListingId,
  onNavigate,
  onPrev,
  onNext,
  containerRef,
  isDisabledScrollIntoView,
  scrollDelay,
  scrollBehavior,
  scrollBlock,
}: Props<T> & Omit<ScrollIntoViewProps, 'scrollTargetQuerySelector'>) => {
  const { scrollTo } = useScrollIntoView({ containerRef });

  const hasPrev = useMemo(
    () => openListingId !== null && listingIds.indexOf(openListingId) > 0,
    [listingIds, openListingId]
  );

  const hasNext = useMemo(
    () => openListingId !== null && listingIds.indexOf(openListingId) < listingIds.length - 1,
    [listingIds, openListingId]
  );

  const handlePrev = useCallback(() => {
    if (openListingId !== null) {
      const currentIndex = listingIds.indexOf(openListingId);
      if (currentIndex > 0) {
        const newIndex = currentIndex - 1;
        onNavigate?.(listingIds[newIndex]);
        onPrev?.();
        scrollToCenter(newIndex);
      }
    }
  }, [listingIds, openListingId]);

  const handleNext = useCallback(() => {
    if (openListingId !== null) {
      const currentIndex = listingIds.indexOf(openListingId);
      if (currentIndex < listingIds.length - 1) {
        const newIndex = currentIndex + 1;
        onNavigate?.(listingIds[newIndex]);
        onNext?.();
        scrollToCenter(newIndex);
      }
    }
  }, [listingIds, openListingId]);

  const scrollToCenter = (index: number) => {
    if (isDisabledScrollIntoView) return;

    scrollTo({
      scrollTargetQuerySelector: `.listing_${listingIds[index]}`,
      scrollDelay,
      scrollBehavior,
      scrollBlock,
    });
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA') return;

      if (openListingId !== null) {
        if (event.key === 'ArrowLeft') {
          handlePrev();
        } else if (event.key === 'ArrowRight') {
          handleNext();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [listingIds, openListingId, onNavigate]);

  return {
    hasPrev,
    hasNext,
    onPrev: handlePrev,
    onNext: handleNext,
  };
};
export type UseListingNavigationType = ReturnType<typeof useListingNavigation>;
